import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import {
  createSessionTokenCache,
  readPersistedSupabaseAccessToken,
  type SessionTokenCache,
} from "./supabase-session-token";

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

/**
 * Browser Supabase client used for authentication (email/password, Google OAuth,
 * password reset, phone/SMS OTP). The session is persisted in localStorage and
 * its access token is attached to tRPC requests (see main.tsx).
 *
 * Returns null when env vars are not configured so the app can still boot and
 * fall back to the legacy auth flow during the transition.
 */
export const supabase: SupabaseClient | null =
  url && anonKey
    ? createClient(url, anonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
      })
    : null;

export function isSupabaseConfigured(): boolean {
  return supabase !== null;
}

const sessionTokenCache: SessionTokenCache | null = supabase
  ? createSessionTokenCache({
      seedToken: readPersistedSupabaseAccessToken(
        typeof localStorage === "undefined" ? null : localStorage,
      ),
      getSession: async () => {
        const { data } = await supabase.auth.getSession();
        return data.session;
      },
      onAuthStateChange: (cb) => {
        const { data } = supabase.auth.onAuthStateChange((_event, session) => {
          cb(session);
        });
        return { unsubscribe: () => data.subscription.unsubscribe() };
      },
    })
  : null;

/** Current Supabase access token (JWT) or null. Waits on getSession when the cache is empty. */
export async function getSupabaseAccessToken(): Promise<string | null> {
  if (!sessionTokenCache) return null;
  return sessionTokenCache.ensureAccessToken();
}
